import { type ReactNode, useEffect, useRef } from 'react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function Modal({ open, onClose, title, children, footer }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (open) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      {/* SCROLLABLE WHEN TALL (2026-08-19). This card had a max-WIDTH and no max-HEIGHT, and the body
          had no overflow — so any modal whose content exceeded the viewport simply grew past it with no
          way to reach the rest, footer buttons included. Surfaced by the blockage queue, where machine
          `detail` is clamped at 4000 characters and routinely overflows, but the defect was never
          specific to that modal: every one of the portal's ~68 Modal consumers had it.

          Three parts, and all three are needed:
            - `max-h-[calc(100vh-2rem)]` gives the card a ceiling with a small viewport margin.
            - `flex flex-col` + `min-h-0` lets the body shrink instead of forcing the card taller than
              its ceiling (without `min-h-0` a flex child refuses to shrink below its content and the
              overflow never engages).
            - `shrink-0` pins the header and footer so the CONTENT scrolls under them — scrolling the
              whole card would move the Cancel/Confirm buttons out of reach, which is the same bug in a
              different place.
          Additive by design: the ceiling only binds when content is tall, so short modals render
          exactly as before. */}
      <div className="relative flex max-h-[calc(100vh-2rem)] flex-col bg-white rounded-lg shadow-lg max-w-lg w-full mx-4 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex shrink-0 items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5">{children}</div>
        {footer && <div className="flex shrink-0 justify-end gap-3 p-5 border-t bg-gray-50 rounded-b-lg">{footer}</div>}
      </div>
    </div>
  );
}
